/* eslint-disable class-methods-use-this */
/* eslint-disable consistent-return */
import axios from "axios";
import { apiUrl } from "../config/urls";
import { computeRequestSig } from "../utils/requestSignature";

const E164_PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

interface OtpErrorDetails {
  status?: number;
  message: string;
}

/**
 * Extracts a human-readable cause from an OTP request failure so callers can
 * surface/log it instead of collapsing everything into a silent "OTP not sent".
 * Distinguishes the backend's 401 "Invalid or missing request signature",
 * 429 rate-limit, 404 (Twilio send failure) and network/CORS errors.
 */
function describeOtpError(error: unknown): OtpErrorDetails {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    return {
      status: error.response?.status,
      message: data?.message || error.message || "Network or server error",
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: "Unknown error" };
}

function normalizePhoneForOtp(rawPhoneNumber: string): string {
  const trimmed = (rawPhoneNumber || "").trim().replace(/\s+/g, "");

  if (!trimmed) {
    throw new Error("Phone number is required");
  }

  let normalized = trimmed;
  if (!normalized.startsWith("+")) {
    if (normalized.startsWith("00")) {
      normalized = `+${normalized.slice(2)}`;
    } else if (normalized.startsWith("0")) {
      normalized = `+27${normalized.slice(1)}`;
    } else {
      normalized = `+${normalized}`;
    }
  }

  if (!E164_PHONE_REGEX.test(normalized)) {
    throw new Error(
      "Phone number must be in international format with country code"
    );
  }

  return normalized;
}

class Auth {
  async login(email: string, password: string) {
    try {
      const response = await axios.post(`${apiUrl}/UserAuth/authorizeUser`, {
        emailAddress: email,
        password,
      });
      if (response.data === 0) return { isSuccess: false };
      return { isSuccess: true, data: response.data };
    } catch (error) {
      return { isSuccess: false };
    }
  }

  async sendOtpVerify(phoneNumber: string) {
    try {
      const normalizedPhoneNumber = normalizePhoneForOtp(phoneNumber);
      const queryParams = new URLSearchParams({
        phoneNumber: normalizedPhoneNumber,
      });
      const sig = await computeRequestSig(normalizedPhoneNumber);

      const response = await axios.get(
        `${apiUrl}/RequestValidation/SendSMSVerify?${queryParams.toString()}`,
        {
          headers: {
            "X-API-Version": "2.0",
            "X-API-Client": "SaferCity",
            "X-Platform": "Web",
            "X-Tenant": "SaferCity",
            ...(sig ? { "X-Request-Sig": sig } : {}),
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        return { data: response.data, error: null as OtpErrorDetails | null };
      }
      return { data: null, error: null as OtpErrorDetails | null };
    } catch (error) {
      const details = describeOtpError(error);
      // eslint-disable-next-line no-console
      console.error("sendOtpVerify failed:", details);
      return { data: null, error: details };
    }
  }

  async verifyOtp(phoneNumber: string, otp: string) {
    try {
      const normalizedPhoneNumber = normalizePhoneForOtp(phoneNumber);
      const sig = await computeRequestSig(normalizedPhoneNumber);

      const response = await axios.post(
        `${apiUrl}/RequestValidation/CheckVerify`,
        {
          phoneNumber: normalizedPhoneNumber,
          otpCode: otp,
        },
        {
          headers: {
            "X-API-Version": "2.0",
            "X-API-Client": "SaferCity",
            "X-Platform": "Web",
            "X-Tenant": "SaferCity",
            ...(sig ? { "X-Request-Sig": sig } : {}),
          },
        }
      );

      if (
        !!response &&
        response.status >= 200 &&
        response.status < 300 &&
        response.data === "approved"
      ) {
        return { isSuccess: true };
      }
      return { isSuccess: false };
    } catch (error) {
      const details = describeOtpError(error);
      // eslint-disable-next-line no-console
      console.error("verifyOtp failed:", details);
      return { isSuccess: false };
    }
  }
}

export default new Auth();
